import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import type { ValidationError } from "~/common/types";
import {
  GetProductsDocument,
  UpsertProductDocument,
  type UpsertProductMutation,
  type UpsertProductMutationVariables,
} from "~/features/admin/products/gqls";
import type { ProductFormData } from "~/features/admin/products/types";
import { translateError } from "~/lib/errors";

interface UseProductCreateOptions {
  onSuccess?: (data: ProductFormData) => void;
  onError?: (error: Pick<ValidationError, "fields">) => void;
}

export function useProductCreate({ onSuccess, onError }: UseProductCreateOptions = {}) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [upsertProduct] = useMutation<UpsertProductMutation, UpsertProductMutationVariables>(UpsertProductDocument);

  const submit = useCallback(
    async (formData: ProductFormData) => {
      setProcessing(true);
      try {
        const res = await upsertProduct({
          variables: {
            input: {
              name: formData.name,
              originHosts: formData.originHosts,
              importDirectWebhookUrl: formData.importDirectWebhookUrl || null,
            },
          },
          refetchQueries: [GetProductsDocument],
        });
        match(res.data?.cnutUpsertProduct)
          .with({ __typename: "ValidationError" }, (e) => onError?.(e))
          .with({ __typename: "Product" }, () => {
            toast.success(t("feature.common.createdSuccessfully", { model: "product" }));
            onSuccess?.(formData);
          })
          .otherwise(() => {});
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [upsertProduct, t, onSuccess, onError],
  );

  return { submit, processing };
}
